import type { CacheValue } from './type';

import { useRef, useCallback, useEffect } from 'react';

// ---------------------------------------------------------------------------
// useRetryScheduler
// Retries onRetry(requestId) every `interval` ms (up to `maxRetries` times)
// as long as the requestId still exists in cache with no data.
// ---------------------------------------------------------------------------
export function useRetryScheduler<T>(
  cache: Record<string, CacheValue<T>>,
  onRetry: (requestId: string) => void,
  options: { interval: number; maxRetries: number },
) {
  const { interval, maxRetries } = options;

  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const cacheRef = useRef(cache);
  cacheRef.current = cache;

  const cancel = useCallback((requestId: string) => {
    const timer = timers.current.get(requestId);
    if (!timer) return;
    clearTimeout(timer);
    timers.current.delete(requestId);
  }, []);

  const schedule = useCallback(
    (requestId: string, attempt = 1) => {
      if (attempt > maxRetries) return;

      const timer = setTimeout(() => {
        timers.current.delete(requestId);

        const entry = Object.values(cacheRef.current).find((v) => v.requestId === requestId);
        if (!entry || entry.data != null) return;

        onRetry(requestId);
        schedule(requestId, attempt + 1);
      }, interval);

      timers.current.set(requestId, timer);
    },
    [interval, maxRetries, onRetry],
  );

  useEffect(() => {
    const timersOnMount = timers.current;
    return () => {
      timersOnMount.forEach(clearTimeout);
      timersOnMount.clear();
    };
  }, []);

  return { schedule, cancel };
}
